import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import SiteFooter from '../components/SiteFooter';
import InfrastructureBackground from '../components/experience/InfrastructureBackground';
import { CheckCircle, ArrowRight, Clock, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PilotCheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') || '';
  const [status, setStatus] = useState('checking'); // checking | paid | pending | error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let cancelled = false;
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        setAttempts(a => a + 1);
        if (r.data.payment_status === 'paid') { setStatus('paid'); return; }
        if (attempts > 20) { setStatus('pending'); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus('error');
      }
    };
    poll();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="landing-page">
      <InfrastructureBackground />
      <section className="section-padding" style={{ minHeight: '80vh' }}>
        <div className="max-w-[720px] mx-auto px-6 text-center">
          <span className="eyebrow"><span className="dot" />PILOT PAYMENT</span>
          {status === 'checking' && (
            <>
              <h1 className="section-title mt-6"><em>Confirming Your Payment.</em></h1>
              <div className="mt-8 flex justify-center"><RefreshCw className="w-8 h-8 text-[color:var(--gold-bright)] animate-spin" /></div>
              <p className="section-kicker mt-6 mx-auto">
                We&rsquo;re verifying with Stripe. This usually takes a few seconds.
              </p>
            </>
          )}
          {status === 'paid' && (
            <>
              <h1 className="section-title mt-6">Payment Received. <em>Welcome To YOVAI.</em></h1>
              <div className="mt-8 flex justify-center">
                <CheckCircle className="w-14 h-14" style={{ color: 'var(--gold-bright)' }} data-testid="pilot-success-check" />
              </div>
              <p className="section-kicker mt-6 mx-auto">
                Your 14-day HVAC pilot begins the moment your customized system is
                configured, approved, and live. Our team will reach out within one business day
                to kick off onboarding and share your signed pilot agreement.
              </p>
              <div className="mt-10 flex flex-wrap gap-3 justify-center">
                <Link to="/" className="btn-primary" data-testid="pilot-success-home">
                  Back To YOVAI <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          )}
          {status === 'pending' && (
            <>
              <h1 className="section-title mt-6">Payment <em>Processing.</em></h1>
              <div className="mt-8 flex justify-center"><Clock className="w-12 h-12 text-[color:var(--gold-bright)]" /></div>
              <p className="section-kicker mt-6 mx-auto">
                Stripe is still confirming your payment. You&rsquo;ll receive an email once it
                settles. You can safely close this page.
              </p>
            </>
          )}
          {status === 'error' && (
            <>
              <h1 className="section-title mt-6">Session <em>Not Found.</em></h1>
              <p className="section-kicker mt-6 mx-auto">
                We couldn&rsquo;t verify this checkout session. If you just paid, please forward
                your Stripe receipt to <a href="mailto:yovel@yovaiagency.com" className="disclosure-link">yovel@yovaiagency.com</a> and we&rsquo;ll confirm from our side.
              </p>
              <div className="mt-8">
                <Link to="/" className="btn-secondary">Back To YOVAI <ArrowRight className="w-4 h-4" /></Link>
              </div>
            </>
          )}
          <p className="mt-8 text-xs text-[color:var(--text-muted)]" data-testid="pilot-success-session">
            Session: {sessionId || '—'}
          </p>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default PilotCheckoutSuccess;
